import {memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Typography} from "antd";
import PropTypes from "prop-types";
import {computeMarqueeLayout} from "./marqueeLayout";

const {Title} = Typography;

// Hard ceiling on distinct rows. The list is decorative — it advertises that a
// big pool exists, it does not enumerate it.
const MAX_ITEMS = 60;

const ParticipantItem = memo(({value, isWinner}) => (
    <div className={`participant-row ${isWinner ? 'participant-row-winner' : ''}`}>
        <span className="truncate">{value}</span>
        {isWinner && <span>🏆</span>}
    </div>
));
ParticipantItem.displayName = 'ParticipantItem';

const ParticipantMarquee = ({lotteries, winnerValue, paused, scrollSpeed}) => {
    const viewportRef = useRef(null);
    const trackRef = useRef(null);
    const [loopDistance, setLoopDistance] = useState(0);
    const [copies, setCopies] = useState(2);

    const items = useMemo(() => {
        if (lotteries.length <= MAX_ITEMS) return lotteries;
        const step = Math.floor(lotteries.length / MAX_ITEMS);
        const sampled = [];
        for (let i = 0; i < lotteries.length && sampled.length < MAX_ITEMS; i += step) {
            sampled.push(lotteries[i]);
        }
        return sampled;
    }, [lotteries]);

    /**
     * Measure a single row, then derive the loop from arithmetic rather than from
     * a subtree's scrollHeight — which is what previously drifted.
     */
    const measure = useCallback(() => {
        const viewport = viewportRef.current;
        const firstRow = trackRef.current?.firstElementChild;
        if (!viewport || !firstRow || items.length === 0) {
            setLoopDistance(0);
            return;
        }

        const rowHeight = firstRow.getBoundingClientRect().height;
        if (rowHeight === 0) return;

        const layout = computeMarqueeLayout({
            itemCount: items.length,
            rowHeight,
            viewportHeight: viewport.clientHeight,
        });
        setLoopDistance(layout.loopDistance);
        setCopies(layout.copies);
    }, [items]);

    useLayoutEffect(() => {
        const frame = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(frame);
    }, [measure]);

    useEffect(() => {
        let frame = 0;
        const onResize = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(measure);
        };
        window.addEventListener("resize", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            cancelAnimationFrame(frame);
        };
    }, [measure]);

    const rows = useMemo(() => {
        const output = [];
        for (let copy = 0; copy < copies; copy++) {
            for (let index = 0; index < items.length; index++) {
                output.push(
                    <ParticipantItem
                        key={`${copy}-${index}`}
                        value={items[index]}
                        isWinner={items[index] === winnerValue}
                    />
                );
            }
        }
        return output;
    }, [items, copies, winnerValue]);

    return (
        <div
            className="h-full backdrop-blur-md rounded-lg flex flex-col overflow-hidden"
            style={{
                background: 'var(--draw-surface)',
                border: '1px solid var(--draw-line)',
                boxShadow: '0 24px 60px rgba(0, 0, 0, 0.55)',
            }}
        >
            <div
                className="p-3"
                style={{background: 'linear-gradient(90deg, rgba(169, 118, 42, 0.55), rgba(232, 193, 112, 0.22))'}}
            >
                <Title level={5} className="mb-0" style={{color: 'var(--draw-gold-bright)'}}>
                    <span aria-hidden="true">📋 </span>Урамшууллын эрхүүд
                </Title>
            </div>

            {/* Decorative: an endlessly scrolling sample of the pool. It conveys
                nothing a screen reader needs and is hostile to read linearly. */}
            <div ref={viewportRef} className="marquee-viewport marquee-pause-on-hover" aria-hidden="true">
                <div
                    ref={trackRef}
                    className="marquee-track marquee-y"
                    style={{
                        "--loop-dist": `${loopDistance}px`,
                        animationDuration: `${scrollSpeed}s`,
                        animationPlayState: paused || loopDistance === 0 ? "paused" : "running",
                    }}
                >
                    {rows}
                </div>

                <div className="pointer-events-none absolute top-0 left-0 right-0 h-8 z-10" style={{background: 'linear-gradient(to bottom, rgba(20, 14, 9, 0.95), transparent)'}}/>
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 z-10" style={{background: 'linear-gradient(to top, rgba(20, 14, 9, 0.95), transparent)'}}/>
            </div>
        </div>
    );
};

ParticipantMarquee.propTypes = {
    lotteries: PropTypes.arrayOf(PropTypes.string).isRequired,
    winnerValue: PropTypes.string,
    paused: PropTypes.bool,
    scrollSpeed: PropTypes.number,
};

export default memo(ParticipantMarquee);
